/**
 * TrialExtensionBanner
 * Compact banner offering the trial extension. Rendered in normal document flow
 * above the top bar (not fixed), so it never covers dialogs and stays small on
 * mobile. Dismissible for the browser session; the top bar action and the
 * Overview card keep the offer reachable afterwards.
 */

import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { useTrialExtension } from "../../../hooks/useTrialExtension";
import { TRIAL_EXTENSION_TOPBAR_ACTION_ID } from "../../../lib/trialExtension";
import { Button, IconButton } from "../UIElements/Button";
import { Icon } from "../UIElements/Icon";

const TrialExtensionBanner = () => {
  const { t } = useTranslation(["billing"]);
  const titleId = useId();
  const {
    available,
    bannerDismissed,
    days,
    daysRemaining,
    dismissBanner,
    openBooking,
  } = useTrialExtension();

  if (!available || bannerDismissed) return null;

  // The dismiss button disappears with the banner: hand focus to the top bar
  // action (the offer stays reachable there) or to the main content, not body.
  const handleDismiss = () => {
    const target =
      document.getElementById(TRIAL_EXTENSION_TOPBAR_ACTION_ID) ||
      document.querySelector("main");
    if (target) {
      if (target.tagName === "MAIN" && !target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
      target.focus({ preventScroll: true });
    }
    dismissBanner();
  };

  return (
    <section
      aria-labelledby={titleId}
      className="border-info/30 text-foreground flex flex-shrink-0 items-center gap-2 border-b bg-[var(--info-bg)] px-3 py-1.5 sm:gap-3 sm:px-4"
      data-testid="trial-extension-banner"
    >
      <Icon
        name="Calendar"
        className="text-info hidden h-5 w-5 flex-shrink-0 sm:block"
      />
      {/* Mobile: short days-remaining text + CTA. The heading stays available to
          assistive tech and becomes visible from sm; the long sentence from lg. */}
      <div className="min-w-0 flex-1 text-sm">
        <h2
          id={titleId}
          className="sr-only font-semibold sm:not-sr-only sm:inline"
        >
          {t("trialExtension.title", { days })}
        </h2>{" "}
        <p className="inline" data-testid="trial-extension-banner-days">
          {t("trialExtension.daysLeft", { remaining: daysRemaining })}
        </p>{" "}
        <p
          className="text-muted-foreground hidden lg:inline"
          data-testid="trial-extension-banner-long"
        >
          {t("trialExtension.bannerText", { days })}
        </p>
      </div>
      <Button
        type="button"
        emphasis="solid"
        intent="brand"
        size="touch"
        className="flex-shrink-0"
        onClick={() => openBooking("banner")}
      >
        {t("trialExtension.bookCall")}
      </Button>
      <IconButton
        type="button"
        variant="ghost"
        className="flex-shrink-0"
        onClick={handleDismiss}
        aria-label={t("trialExtension.close")}
      >
        <Icon name="X" className="h-4 w-4" />
      </IconButton>
    </section>
  );
};

export default TrialExtensionBanner;
